import { useTranslation } from 'next-i18next';
import { type BoardTranslationKeys } from '@/types/translation';
import { cn } from '@/utils';
import { BOARD_TEMPLATES } from '../constants';
import type { BoardTemplate } from '../types/template';

// 기존 상대 순서는 유지하되, 그룹형 → 담벼락형 순으로만 정렬 (Array.sort는 안정 정렬).
const ORDERED_TEMPLATES = [...BOARD_TEMPLATES].sort((a, b) => Number(b.type === 'GROUP') - Number(a.type === 'GROUP'));
const boardTypePreviewMotionClassName = 'pointer-events-none absolute left-0 top-[-4px] h-[calc(100%+9px)] w-full object-cover transition-[top] duration-150 ease-out group-hover:top-[-8px] group-focus-visible:top-[-8px] motion-reduce:transition-none';
const templateDescriptionMotionClassName = 'max-h-0 translate-y-1 overflow-hidden break-keep text-caption-md-medium text-alternative opacity-0 transition-[max-height,opacity,transform] duration-150 ease-out group-hover:max-h-[32px] group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:max-h-[32px] group-focus-visible:translate-y-0 group-focus-visible:opacity-100 motion-reduce:transition-none';
interface TemplatePickerProps {
  onSelect: (template: BoardTemplate) => void;
  onCreateBlank: (type: BoardTemplate['type']) => void;
  disabled?: boolean;
}
const BOARD_TYPES: {
  type: BoardTemplate['type'];
  labelKey: BoardTranslationKeys;
  previewClassName: string;
  imageSrc: string;
}[] = [{
  type: 'WALL',
  labelKey: 'myBoard.card.type.wall',
  previewClassName: 'bg-violet-300',
  imageSrc: '/assets/board-template-wall-type@2x.webp'
}, {
  type: 'GROUP',
  labelKey: 'board.group.button',
  previewClassName: 'bg-blue-300',
  imageSrc: '/assets/board-template-group-type@2x.webp'
}];
export function TemplatePicker({
  onSelect,
  onCreateBlank,
  disabled
}: TemplatePickerProps) {
  const {
    t
  } = useTranslation('board');
  const {
    t: tTemplate
  } = useTranslation('boardTemplates');
  return <div className="flex flex-col" data-sentry-component="TemplatePicker" data-sentry-source-file="TemplatePicker.tsx">
      <section className="pb-2xl">
        <div className="flex flex-col gap-xs pb-lg">
          <h2 className="text-heading-lg-extrabold text-default">
            {t('board.new.blank.title')}
          </h2>
          <p className="text-body-md-medium text-neutral">
            {t('board.new.blank.description')}
          </p>
        </div>

        <ul className="grid max-w-[430px] grid-cols-2 gap-md">
          {BOARD_TYPES.map(({
          type,
          labelKey,
          previewClassName,
          imageSrc
        }) => <li key={type}>
              <button type="button" disabled={disabled} onClick={() => onCreateBlank(type)} className={cn('group flex w-full min-w-[160px] flex-col items-start gap-sm text-left disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50')}>
                <span className={cn('relative flex aspect-[206/132] w-full items-center justify-center overflow-hidden rounded-md border-4 border-opacity-white-6 shadow-sm transition-shadow duration-150 ease-out group-hover:shadow-md group-focus-visible:shadow-md motion-reduce:transition-none', previewClassName)}>
                  <img src={imageSrc} alt="" className={boardTypePreviewMotionClassName} />
                  <span className="absolute inset-[-4px] bg-opacity-white-3 opacity-0 transition-opacity duration-150 ease-out group-hover:opacity-100 group-focus-visible:opacity-100 motion-reduce:transition-none" />
                </span>
                <span className="w-full px-xs text-body-md-semibold text-default transition-colors duration-150 ease-out group-hover:text-neutral group-focus-visible:text-neutral motion-reduce:transition-none">
                  {t(labelKey)}
                </span>
              </button>
            </li>)}
        </ul>
      </section>

      <section>
        <div className="flex flex-col gap-xs pb-lg">
          <h2 className="text-heading-lg-extrabold text-default">
            {t('board.new.template.title')}
          </h2>
          <p className="text-body-md-medium text-neutral">
            {t('board.new.template.description')}
          </p>
        </div>

        <ul className="grid grid-cols-1 gap-md tablet-sm:grid-cols-3">
          {ORDERED_TEMPLATES.map(template => <li key={template.id}>
              <button type="button" disabled={disabled} onClick={() => onSelect(template)} className={cn('group relative flex h-[78px] w-full items-center gap-sm overflow-hidden rounded-md border border-assistive bg-default px-md py-sm text-left shadow-xs transition-shadow duration-150 ease-out enabled:hover:shadow-sm enabled:focus-visible:shadow-sm disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none')}>
                <span aria-hidden="true" className="absolute right-[15px] top-[11px] rounded-full border border-alternative bg-default px-2xs text-caption-md-semibold text-assistive">
                  {template.type === 'GROUP' ? t('board.group.button') : t('myBoard.card.type.wall')}
                </span>
                <span className="relative size-[54px] shrink-0 overflow-hidden rounded-sm border border-opacity-assistive bg-strong-alternative">
                  {template.thumbnail ? <img src={template.thumbnail} alt="" className="h-full w-full object-cover" /> : null}
                  <span className="absolute inset-[-1px] bg-opacity-white-4 opacity-0 transition-opacity duration-150 ease-out group-hover:opacity-100 group-focus-visible:opacity-100 motion-reduce:transition-none" />
                </span>
                <span className="flex min-w-0 flex-1 flex-col justify-center gap-0 transition-[gap] duration-150 ease-out group-hover:gap-4xs group-focus-visible:gap-4xs motion-reduce:transition-none">
                  <span className="truncate text-body-md-semibold text-default">
                    {tTemplate(template.nameKey)}
                  </span>
                  <span className={templateDescriptionMotionClassName}>
                    {tTemplate(template.descriptionKey)}
                  </span>
                </span>
              </button>
            </li>)}
        </ul>
      </section>
    </div>;
}